import React, { useState, useEffect } from 'react';
import {
    X,
    History,
    Loader2,
    RefreshCw,
    CheckCircle,
    Clock,
    FileText,
    Mail,
    ChevronUp,
    ChevronDown,
} from 'lucide-react';
import { apiClient, RecentActivity, BulkOperationSummary, FleetSubtype } from '../../services/api';
import BulkCheckResultsModal from './BulkCheckResultsModal';
import MultiSelectFilter from '../MultiSelectFilter';

interface DriversHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    subtype?: FleetSubtype;
}

type Tab = 'checks' | 'operations';

const DriversHistoryModal: React.FC<DriversHistoryModalProps> = ({ isOpen, onClose, subtype }) => {
    const [activeTab, setActiveTab] = useState<Tab>('checks');

    // Licence checks state (single + bulk via getRecentActivity)
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [isLoadingChecks, setIsLoadingChecks] = useState(false);
    const [checksError, setChecksError] = useState<string | null>(null);
    const [checksPage, setChecksPage] = useState(1);
    const [checksTotal, setChecksTotal] = useState(0);
    const CHECKS_PAGE_SIZE = 15;
    type ChecksSortCol = 'driverName' | 'checkDate' | 'riskScore' | 'status';
    const [checksSortCol, setChecksSortCol] = useState<ChecksSortCol>('checkDate');
    const [checksSortDir, setChecksSortDir] = useState<'asc' | 'desc'>('desc');

    // Filters
    const [checksSearch, setChecksSearch] = useState('');
    const [checksRisk, setChecksRisk] = useState('all');
    const [checksType, setChecksType] = useState('all');
    const [checksResult, setChecksResult] = useState('all');

    // Bulk operations state
    const [operations, setOperations] = useState<BulkOperationSummary[]>([]);
    const [isLoadingOps, setIsLoadingOps] = useState(false);
    const [opsError, setOpsError] = useState<string | null>(null);
    const OPS_PAGE_SIZE = 15;
    const [opsPage, setOpsPage] = useState(1);
    const [opsTotal, setOpsTotal] = useState(0);
    type OpsSortCol = 'completedAt' | 'type' | 'totalItems';
    const [opsSortCol, setOpsSortCol] = useState<OpsSortCol>('completedAt');
    const [opsSortDir, setOpsSortDir] = useState<'ASC' | 'DESC'>('DESC');
    const [opsType, setOpsType] = useState('all');
    const [opsDateFrom, setOpsDateFrom] = useState('');
    const [opsDateTo, setOpsDateTo] = useState('');

    // Results modal (licence checks)
    const [selectedOperationId, setSelectedOperationId] = useState<string | null>(null);
    const [operationResults, setOperationResults] = useState<any>(null);
    const [isLoadingResults, setIsLoadingResults] = useState(false);
    const [showResultsModal, setShowResultsModal] = useState(false);

    // Driver list panel (consent sends & imports)
    const [showDriverList, setShowDriverList] = useState(false);
    const [driverListData, setDriverListData] = useState<any>(null);
    const [driverListType, setDriverListType] = useState<string>('');
    const [isLoadingDriverList, setIsLoadingDriverList] = useState(false);

    const loadChecks = async (
        page = checksPage,
        filters = { driverName: checksSearch, riskLevel: checksRisk, checkType: checksType, result: checksResult }
    ) => {
        setIsLoadingChecks(true);
        setChecksError(null);
        try {
            const result = await apiClient.getRecentActivity(page, CHECKS_PAGE_SIZE, filters);
            setRecentActivity(result.data);
            setChecksTotal(result.pagination.total);
        } catch (err: any) {
            setChecksError(err.message || 'Failed to load check history');
        } finally {
            setIsLoadingChecks(false);
        }
    };

    const applyFilter = (newFilters: { driverName?: string; riskLevel?: string; checkType?: string; result?: string }) => {
        const filters = {
            driverName: newFilters.driverName ?? checksSearch,
            riskLevel: newFilters.riskLevel ?? checksRisk,
            checkType: newFilters.checkType ?? checksType,
            result: newFilters.result ?? checksResult,
        };
        setChecksPage(1);
        loadChecks(1, filters);
    };

    const loadOperations = async (
        page = opsPage,
        sortCol = opsSortCol,
        sortDir = opsSortDir,
        filters = { type: opsType, dateFrom: opsDateFrom, dateTo: opsDateTo }
    ) => {
        setIsLoadingOps(true);
        setOpsError(null);
        try {
            const params: Parameters<typeof apiClient.getBulkOperations>[0] = {
                status: 'completed',
                limit: OPS_PAGE_SIZE,
                page,
                sortBy: sortCol,
                sortOrder: sortDir,
            };
            if (filters.type && filters.type !== 'all') params.type = filters.type as any;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters.dateTo) params.dateTo = filters.dateTo;
            const response = await apiClient.getBulkOperations(params);
            setOperations(response.data);
            setOpsTotal(response.totalCount);
        } catch (err: any) {
            setOpsError(err.message || 'Failed to load operations history');
        } finally {
            setIsLoadingOps(false);
        }
    };

    const applyOpsFilter = (newFilters: { type?: string; dateFrom?: string; dateTo?: string }) => {
        const filters = {
            type: newFilters.type ?? opsType,
            dateFrom: newFilters.dateFrom ?? opsDateFrom,
            dateTo: newFilters.dateTo ?? opsDateTo,
        };
        setOpsPage(1);
        loadOperations(1, opsSortCol, opsSortDir, filters);
    };

    const handleOpsSort = (col: OpsSortCol) => {
        const newDir = col === opsSortCol ? (opsSortDir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
        setOpsSortCol(col);
        setOpsSortDir(newDir);
        setOpsPage(1);
        loadOperations(1, col, newDir, { type: opsType, dateFrom: opsDateFrom, dateTo: opsDateTo });
    };

    const handleOpsPageChange = (newPage: number) => {
        setOpsPage(newPage);
        loadOperations(newPage);
    };

    useEffect(() => {
        if (isOpen) {
            setChecksPage(1);
            setChecksSearch('');
            setChecksRisk('all');
            setChecksType('all');
            setChecksResult('all');
            loadChecks(1, { driverName: '', riskLevel: 'all', checkType: 'all', result: 'all' });
            setOpsPage(1);
            setOpsType('all');
            setOpsDateFrom('');
            setOpsDateTo('');
            setOpsSortCol('completedAt');
            setOpsSortDir('DESC');
            loadOperations(1, 'completedAt', 'DESC', { type: 'all', dateFrom: '', dateTo: '' });
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChecksPageChange = (newPage: number) => {
        setChecksPage(newPage);
        loadChecks(newPage);
    };

    const handleViewResults = async (operationId: string) => {
        setSelectedOperationId(operationId);
        setIsLoadingResults(true);
        try {
            const results = await apiClient.getBulkOperationResults(operationId);
            setOperationResults(results);
            setShowResultsModal(true);
        } catch (err: any) {
            // handle silently
        } finally {
            setIsLoadingResults(false);
        }
    };

    const handleViewDrivers = async (operationId: string, type: string) => {
        setSelectedOperationId(operationId);
        setDriverListType(type);
        setIsLoadingDriverList(true);
        try {
            const results = await apiClient.getBulkOperationResults(operationId);
            setDriverListData(results.data);
            setShowDriverList(true);
        } catch (err: any) {
            // handle silently
        } finally {
            setIsLoadingDriverList(false);
        }
    };

    const handleChecksSort = (col: ChecksSortCol) => {
        if (col === checksSortCol) {
            setChecksSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setChecksSortCol(col);
            setChecksSortDir('asc');
        }
    };

    const sortedActivity = [...recentActivity].sort((a, b) => {
        let cmp = 0;
        if (checksSortCol === 'driverName') cmp = a.driverName.localeCompare(b.driverName);
        else if (checksSortCol === 'checkDate') cmp = new Date(a.checkDate).getTime() - new Date(b.checkDate).getTime();
        else if (checksSortCol === 'riskScore') cmp = (a.riskScore ?? 0) - (b.riskScore ?? 0);
        else if (checksSortCol === 'status') cmp = a.status.localeCompare(b.status);
        return checksSortDir === 'asc' ? cmp : -cmp;
    });

    if (!isOpen) return null;

    const getRiskBadge = (riskLevel: string) => {
        const barClass = riskLevel === 'high' ? 'bg-red-500' : riskLevel === 'medium' ? 'bg-amber-400' : 'bg-green-500';
        const label = riskLevel ? riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1) : 'Low';
        return (
            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                <span className={`h-4 w-[3px] rounded-full ${barClass}`} aria-hidden="true" />
                {label}
            </span>
        );
    };

    const getOperationTypeLabel = (type: string) => {
        switch (type) {
            case 'licence_check': return { label: 'Licence Check', icon: <CheckCircle className="w-4 h-4 text-green-600" /> };
            case 'automated_licence_check': return { label: 'Automated Licence Check', icon: <Clock className="w-4 h-4 text-gray-500" /> };
            case 'consent_send': return { label: 'Consent Send', icon: <Mail className="w-4 h-4 text-blue-600" /> };
            case 'driver_import': return { label: 'Driver Import', icon: <FileText className="w-4 h-4 text-purple-600" /> };
            default: return { label: type, icon: <Clock className="w-4 h-4 text-gray-500" /> };
        }
    };

    const formatDate = (date: Date | string | null) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="fixed inset-0 bg-black/50" onClick={onClose} />
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 flex flex-col" style={{ height: '75vh' }}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                <History className="w-5 h-5 text-gray-700" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">History</h2>
                                <p className="text-sm text-gray-500">Licence checks, consent sends, and imports</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
                        {([
                            { id: 'checks',     label: 'Licence Checks'  },
                            { id: 'operations', label: 'Bulk Operations' },
                        ] as { id: Tab; label: string }[]).map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-green-500 text-green-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                                style={activeTab === tab.id ? { borderColor: '#69BE28', color: '#4a8a1c' } : {}}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Licence Checks Tab */}
                        {activeTab === 'checks' && (
                            <div>
                                {/* Filter bar */}
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            placeholder="Search driver..."
                                            value={checksSearch}
                                            onChange={e => {
                                                setChecksSearch(e.target.value);
                                                applyFilter({ driverName: e.target.value });
                                            }}
                                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
                                            style={{ '--tw-ring-color': 'rgba(105,190,40,0.5)' } as React.CSSProperties}
                                        />
                                        <div className="w-36">
                                            <MultiSelectFilter
                                                label="Risk level"
                                                allLabel="All risks"
                                                options={[
                                                    { value: 'low', label: 'Low' },
                                                    { value: 'medium', label: 'Medium' },
                                                    { value: 'high', label: 'High' },
                                                ]}
                                                values={checksRisk === 'all' ? [] : [checksRisk]}
                                                onApply={(values) => { const v = values[values.length - 1] ?? 'all'; setChecksRisk(v); applyFilter({ riskLevel: v }); }}
                                            />
                                        </div>
                                        <div className="w-36">
                                            <MultiSelectFilter
                                                label="Check type"
                                                allLabel="All types"
                                                options={[
                                                    { value: 'manual', label: 'Manual' },
                                                    { value: 'automated', label: 'Automated' },
                                                ]}
                                                values={checksType === 'all' ? [] : [checksType]}
                                                onApply={(values) => { const v = values[values.length - 1] ?? 'all'; setChecksType(v); applyFilter({ checkType: v }); }}
                                            />
                                        </div>
                                        <div className="w-36">
                                            <MultiSelectFilter
                                                label="Result"
                                                allLabel="All results"
                                                options={[
                                                    { value: 'valid', label: 'Valid' },
                                                    { value: 'invalid', label: 'Invalid' },
                                                ]}
                                                values={checksResult === 'all' ? [] : [checksResult]}
                                                onApply={(values) => { const v = values[values.length - 1] ?? 'all'; setChecksResult(v); applyFilter({ result: v }); }}
                                            />
                                        </div>
                                        <button
                                            onClick={() => loadChecks(checksPage)}
                                            disabled={isLoadingChecks}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex-shrink-0"
                                            title="Refresh"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${isLoadingChecks ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>

                                    {/* Active filter chips */}
                                    {(() => {
                                        const chips: { key: string; label: string; onRemove: () => void }[] = [];
                                        if (checksSearch) chips.push({
                                            key: 'search',
                                            label: `Search: "${checksSearch}"`,
                                            onRemove: () => { setChecksSearch(''); applyFilter({ driverName: '' }); },
                                        });
                                        if (checksRisk !== 'all') chips.push({
                                            key: 'risk',
                                            label: `Risk: ${checksRisk.charAt(0).toUpperCase() + checksRisk.slice(1)}`,
                                            onRemove: () => { setChecksRisk('all'); applyFilter({ riskLevel: 'all' }); },
                                        });
                                        if (checksType !== 'all') chips.push({
                                            key: 'type',
                                            label: `Type: ${checksType.charAt(0).toUpperCase() + checksType.slice(1)}`,
                                            onRemove: () => { setChecksType('all'); applyFilter({ checkType: 'all' }); },
                                        });
                                        if (checksResult !== 'all') chips.push({
                                            key: 'result',
                                            label: `Result: ${checksResult.charAt(0).toUpperCase() + checksResult.slice(1)}`,
                                            onRemove: () => { setChecksResult('all'); applyFilter({ result: 'all' }); },
                                        });
                                        if (chips.length === 0) return null;
                                        return (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mr-1">Filters</span>
                                                {chips.map(chip => (
                                                    <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-sm font-medium text-gray-700 shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
                                                        {chip.label}
                                                        <button onClick={chip.onRemove} className="relative ml-0.5 rounded-full p-1 text-gray-500 transition-colors before:absolute before:-inset-1.5 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-1">
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    </span>
                                                ))}
                                                {chips.length > 1 && (
                                                    <button
                                                        onClick={() => {
                                                            setChecksSearch(''); setChecksRisk('all'); setChecksType('all'); setChecksResult('all');
                                                            setChecksPage(1);
                                                            loadChecks(1, { driverName: '', riskLevel: 'all', checkType: 'all', result: 'all' });
                                                        }}
                                                        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                                                    >
                                                        Clear all
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>

                                {isLoadingChecks ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                                    </div>
                                ) : checksError ? (
                                    <div className="p-6 text-center text-red-600 text-sm">{checksError}</div>
                                ) : recentActivity.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                        <p className="text-sm text-gray-500">No licence checks found</p>
                                    </div>
                                ) : (
                                    <>
                                        <table className="min-w-full divide-y divide-gray-100">
                                            <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                                <tr>
                                                    {([
                                                        { col: 'driverName', label: 'Driver' },
                                                        { col: null,         label: 'Licence' },
                                                        { col: 'checkDate',  label: 'Date' },
                                                        { col: 'riskScore',  label: 'Risk' },
                                                        { col: 'status',     label: 'Status' },
                                                        { col: null,         label: 'Checked By' },
                                                    ] as { col: ChecksSortCol | null; label: string }[]).map(({ col, label }) => (
                                                        <th
                                                            key={label}
                                                            onClick={col ? () => handleChecksSort(col) : undefined}
                                                            className={`px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider ${col ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`}
                                                        >
                                                            <span className="inline-flex items-center">
                                                                {label}
                                                                {col && checksSortCol === col && (
                                                                    checksSortDir === 'asc'
                                                                        ? <ChevronUp className="w-3 h-3 ml-1" />
                                                                        : <ChevronDown className="w-3 h-3 ml-1" />
                                                                )}
                                                            </span>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {sortedActivity.map((activity) => (
                                                    <tr key={activity.id} className="hover:bg-gray-50/60 transition-colors">
                                                        <td className="px-4 py-3.5 text-sm font-medium text-gray-900">{activity.driverName}</td>
                                                        <td className="px-4 py-3.5 text-sm text-gray-600 font-mono">{activity.licenceNumber}</td>
                                                        <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                                                            {formatDate(activity.checkDate)}
                                                        </td>
                                                        <td className="px-4 py-3.5">{getRiskBadge(activity.riskLevel)}</td>
                                                        <td className="px-4 py-3.5 text-sm text-gray-600">{activity.status}</td>
                                                        <td className="px-4 py-3.5 text-sm text-gray-500">{activity.checkedBy}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {checksTotal > CHECKS_PAGE_SIZE && (
                                            <div className="px-4 py-3.5 border-t border-gray-100 flex items-center justify-between bg-white flex-shrink-0">
                                                <p className="text-xs text-gray-500">
                                                    Showing {((checksPage - 1) * CHECKS_PAGE_SIZE) + 1}-{Math.min(checksPage * CHECKS_PAGE_SIZE, checksTotal)} of {checksTotal}
                                                </p>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleChecksPageChange(checksPage - 1)}
                                                        disabled={checksPage === 1}
                                                        className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        Previous
                                                    </button>
                                                    <span className="px-3 py-1.5 text-xs text-gray-500">
                                                        Page {checksPage} of {Math.ceil(checksTotal / CHECKS_PAGE_SIZE)}
                                                    </span>
                                                    <button
                                                        onClick={() => handleChecksPageChange(checksPage + 1)}
                                                        disabled={checksPage >= Math.ceil(checksTotal / CHECKS_PAGE_SIZE)}
                                                        className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Bulk Operations Tab */}
                        {activeTab === 'operations' && (
                            <div>
                                {/* Filter bar */}
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-44">
                                            <MultiSelectFilter
                                                label="Operation type"
                                                allLabel="All types"
                                                options={[
                                                    { value: 'licence_check', label: 'Bulk Check' },
                                                    { value: 'automated_licence_check', label: 'Automated Check' },
                                                    { value: 'consent_send', label: 'Consent Send' },
                                                    { value: 'driver_import', label: 'Driver Import' },
                                                ]}
                                                values={opsType === 'all' ? [] : [opsType]}
                                                onApply={(values) => { const v = values[values.length - 1] ?? 'all'; setOpsType(v); applyOpsFilter({ type: v }); }}
                                            />
                                        </div>
                                        <input
                                            type="date"
                                            value={opsDateFrom}
                                            onChange={e => { setOpsDateFrom(e.target.value); applyOpsFilter({ dateFrom: e.target.value }); }}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
                                            style={{ '--tw-ring-color': 'rgba(105,190,40,0.5)' } as React.CSSProperties}
                                            title="From date"
                                        />
                                        <span className="text-xs text-gray-400 flex-shrink-0">to</span>
                                        <input
                                            type="date"
                                            value={opsDateTo}
                                            onChange={e => { setOpsDateTo(e.target.value); applyOpsFilter({ dateTo: e.target.value }); }}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
                                            style={{ '--tw-ring-color': 'rgba(105,190,40,0.5)' } as React.CSSProperties}
                                            title="To date"
                                        />
                                        <div className="flex-1" />
                                        <button
                                            onClick={() => loadOperations(opsPage)}
                                            disabled={isLoadingOps}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex-shrink-0"
                                            title="Refresh"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${isLoadingOps ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>

                                    {/* Active filter chips */}
                                    {(() => {
                                        const typeLabels: Record<string, string> = {
                                            licence_check: 'Bulk Check',
                                            automated_licence_check: 'Automated Check',
                                            consent_send: 'Consent Send',
                                            driver_import: 'Driver Import',
                                        };
                                        const chips: { key: string; label: string; onRemove: () => void }[] = [];
                                        if (opsType !== 'all') chips.push({
                                            key: 'type',
                                            label: `Type: ${typeLabels[opsType] ?? opsType}`,
                                            onRemove: () => { setOpsType('all'); applyOpsFilter({ type: 'all' }); },
                                        });
                                        if (opsDateFrom) chips.push({
                                            key: 'from',
                                            label: `From: ${opsDateFrom}`,
                                            onRemove: () => { setOpsDateFrom(''); applyOpsFilter({ dateFrom: '' }); },
                                        });
                                        if (opsDateTo) chips.push({
                                            key: 'to',
                                            label: `To: ${opsDateTo}`,
                                            onRemove: () => { setOpsDateTo(''); applyOpsFilter({ dateTo: '' }); },
                                        });
                                        if (chips.length === 0) return null;
                                        return (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mr-1">Filters</span>
                                                {chips.map(chip => (
                                                    <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-sm font-medium text-gray-700 shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
                                                        {chip.label}
                                                        <button onClick={chip.onRemove} className="relative ml-0.5 rounded-full p-1 text-gray-500 transition-colors before:absolute before:-inset-1.5 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-1">
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    </span>
                                                ))}
                                                {chips.length > 1 && (
                                                    <button
                                                        onClick={() => {
                                                            setOpsType('all'); setOpsDateFrom(''); setOpsDateTo('');
                                                            setOpsPage(1);
                                                            loadOperations(1, opsSortCol, opsSortDir, { type: 'all', dateFrom: '', dateTo: '' });
                                                        }}
                                                        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                                                    >
                                                        Clear all
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>

                                {isLoadingOps ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                                    </div>
                                ) : opsError ? (
                                    <div className="p-6 text-center text-red-600 text-sm">{opsError}</div>
                                ) : operations.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                        <p className="text-sm text-gray-500">No bulk operations found</p>
                                    </div>
                                ) : (
                                    <>
                                        <table className="min-w-full divide-y divide-gray-100">
                                            <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                                <tr>
                                                    {([
                                                        { col: 'type',        label: 'Type' },
                                                        { col: 'completedAt', label: 'Date' },
                                                        { col: 'totalItems',  label: 'Drivers' },
                                                        { col: null,          label: 'Successful' },
                                                        { col: null,          label: 'Failed' },
                                                        { col: null,          label: 'Actions' },
                                                    ] as { col: OpsSortCol | null; label: string }[]).map(({ col, label }) => (
                                                        <th
                                                            key={label}
                                                            onClick={col ? () => handleOpsSort(col) : undefined}
                                                            className={`px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider ${col ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`}
                                                        >
                                                            <span className="inline-flex items-center">
                                                                {label}
                                                                {col && opsSortCol === col && (
                                                                    opsSortDir === 'ASC'
                                                                        ? <ChevronUp className="w-3 h-3 ml-1" />
                                                                        : <ChevronDown className="w-3 h-3 ml-1" />
                                                                )}
                                                            </span>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {operations.map((op) => {
                                                    const typeInfo = getOperationTypeLabel(op.type);
                                                    return (
                                                        <tr key={op.id} className="hover:bg-gray-50/60 transition-colors">
                                                            <td className="px-4 py-3.5">
                                                                <div className="flex items-center space-x-2">
                                                                    {typeInfo.icon}
                                                                    <span className="text-sm font-medium text-gray-900">{typeInfo.label}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                                                                {formatDate(op.completedAt)}
                                                            </td>
                                                            <td className="px-4 py-3.5 text-sm text-gray-900">{op.totalItems}</td>
                                                            <td className="px-4 py-3.5">
                                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                    <span className="h-4 w-[3px] rounded-full bg-green-500" aria-hidden="true" />{op.successCount}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                {op.failureCount > 0 ? (
                                                                    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                        <span className="h-4 w-[3px] rounded-full bg-red-500" aria-hidden="true" />{op.failureCount}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-xs text-gray-400">-</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                {(op.type === 'licence_check' || op.type === 'automated_licence_check') && (
                                                                    <button
                                                                        onClick={() => handleViewResults(op.id)}
                                                                        disabled={isLoadingResults && selectedOperationId === op.id}
                                                                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                                    >
                                                                        {isLoadingResults && selectedOperationId === op.id ? (
                                                                            <Loader2 className="w-3 h-3 animate-spin inline" />
                                                                        ) : 'View Results'}
                                                                    </button>
                                                                )}
                                                                {(op.type === 'consent_send' || op.type === 'driver_import') && (
                                                                    <button
                                                                        onClick={() => handleViewDrivers(op.id, op.type)}
                                                                        disabled={isLoadingDriverList && selectedOperationId === op.id}
                                                                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                                    >
                                                                        {isLoadingDriverList && selectedOperationId === op.id ? (
                                                                            <Loader2 className="w-3 h-3 animate-spin inline" />
                                                                        ) : 'View'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        {opsTotal > OPS_PAGE_SIZE && (
                                            <div className="px-4 py-3.5 border-t border-gray-100 flex items-center justify-between bg-white flex-shrink-0">
                                                <p className="text-xs text-gray-500">
                                                    Showing {((opsPage - 1) * OPS_PAGE_SIZE) + 1}-{Math.min(opsPage * OPS_PAGE_SIZE, opsTotal)} of {opsTotal}
                                                </p>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleOpsPageChange(opsPage - 1)}
                                                        disabled={opsPage === 1}
                                                        className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        Previous
                                                    </button>
                                                    <span className="px-3 py-1.5 text-xs text-gray-500">
                                                        Page {opsPage} of {Math.ceil(opsTotal / OPS_PAGE_SIZE)}
                                                    </span>
                                                    <button
                                                        onClick={() => handleOpsPageChange(opsPage + 1)}
                                                        disabled={opsPage >= Math.ceil(opsTotal / OPS_PAGE_SIZE)}
                                                        className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Driver List Panel - consent sends & imports */}
            {showDriverList && driverListData && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                    {driverListType === 'consent_send' ? 'Consent Send' : 'Driver Import'}
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {driverListData.operation.totalItems} drivers &middot; {driverListData.operation.successCount} succeeded &middot; {driverListData.operation.failureCount} failed
                                </p>
                            </div>
                            <button
                                onClick={() => { setShowDriverList(false); setDriverListData(null); setSelectedOperationId(null); }}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-100">
                                <thead className="bg-gray-50/80 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {driverListData.items.map((item: any) => (
                                        <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                                            <td className="px-4 py-3.5 text-sm font-medium text-gray-900">
                                                {item.driver ? `${item.driver.firstName} ${item.driver.lastName}` : '-'}
                                            </td>
                                            <td className="px-4 py-3.5 text-sm text-gray-500">
                                                {item.driver?.email ?? '-'}
                                            </td>
                                            <td className="px-4 py-3.5">
                                                {item.status === 'completed' ? (
                                                    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                        <span className="h-4 w-[3px] rounded-full bg-green-500" aria-hidden="true" />
                                                        {driverListType === 'consent_send' ? 'Sent' : 'Imported'}
                                                    </span>
                                                ) : item.status === 'failed' ? (
                                                    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700" title={item.errorMessage ?? ''}>
                                                        <span className="h-4 w-[3px] rounded-full bg-red-500" aria-hidden="true" />
                                                        Failed
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400 capitalize">{item.status}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Check Results Modal */}
            <BulkCheckResultsModal
                isOpen={showResultsModal}
                onClose={() => {
                    setShowResultsModal(false);
                    setOperationResults(null);
                    setSelectedOperationId(null);
                }}
                results={operationResults}
                subtype={subtype}
            />
        </>
    );
};

export default DriversHistoryModal;
